import { useMemo } from 'react';

import { quarterLabel, rollingQuarters, toIsoDate } from '@/lib/eos/quarters';
import { cn } from '@/lib/cn';

interface QuarterPickerProps {
  /** ISO date string (YYYY-MM-DD) of the selected quarter start. */
  value: string;
  onChange: (isoQuarterStart: string) => void;
  /** How many rolling quarters to offer (current + preceding). */
  count?: number;
  className?: string;
}

/**
 * Controlled dropdown of the current quarter plus the preceding ones
 * (newest first), labeled "Q3 2026"-style. The value round-trips as the
 * YYYY-MM-DD quarter boundary the EOS API expects.
 */
export function QuarterPicker({ value, onChange, count = 6, className }: QuarterPickerProps) {
  const options = useMemo(() => {
    const quarters = rollingQuarters(new Date(), count).map((d) => ({
      iso: toIsoDate(d),
      label: quarterLabel(d),
    }));
    // Keep an out-of-window selection (e.g. deep link to an old quarter)
    // visible instead of silently snapping the select to something else.
    if (value && !quarters.some((q) => q.iso === value)) {
      const date = new Date(`${value}T00:00:00.000Z`);
      if (!Number.isNaN(date.valueOf())) {
        quarters.push({ iso: value, label: quarterLabel(date) });
      }
    }
    return quarters;
  }, [count, value]);

  return (
    <select
      aria-label="Quarter"
      value={value}
      onChange={(e) => {
        onChange(e.target.value);
      }}
      className={cn(
        'rounded-kw-md border border-kw-border bg-white px-2.5 py-1.5 text-[13px] text-kw-shark',
        'focus:border-kw-cerulean focus:outline-none focus:ring-2 focus:ring-kw-cerulean/20',
        className,
      )}
    >
      {options.map((q) => (
        <option key={q.iso} value={q.iso}>
          {q.label}
        </option>
      ))}
    </select>
  );
}
